import classNames from "classnames"
import React from "react"

import BulletListCell from "./BulletListCell"
import ButtonCell from "./ButtonCell"
import IconCell from "./IconCell"
import ListCell from "./ListCell"
import PriceCell from "./PriceCell"
import SimplePriceFromStoryblokSlug from "./SimplePriceFromStoryblokSlug"
import TextLinkCell from "./TextLinkCell"
import {
  extractFromTag,
  getThemeColor,
  getCellBackgroundClass,
  parseContent,
  extractPriceData,
  parseButtonData,
  getCellRoundingClasses,
  matchCellPatterns,
  parseTextLinkData,
  extractSimplePriceData,
} from "./utils"
import { Color } from "../../../../constants/V2/color"
import Typography from "../../../elements/V2/Typography"

import { TableRegex } from "@constants/tables"
import { useGetProductBySlug } from "@hooks/V2/storyblok/useProduct"
import { getAccentColor } from "@utils/V2/color"

const CELL_CLASSES = "h-full text-center flex px-16 py-20 rounded-10"
export const CELL_TEXT_CLASSSES = "text-start"

type CellDataProps = {
  rowIndex: number
  columnIndex: number
  displayIndex: number
  data: string[][]
  rowHeadings: string[]
  tableTheme: "light" | "dark"
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  maxMobileColumns: number
  maxDesktopColumns: number
  titleRowIndices: React.MutableRefObject<number[]>
  className?: string
  variant: "singleEntry" | "doubleEntry"
}

const CellData = ({
  rowIndex,
  columnIndex,
  displayIndex,
  data,
  rowHeadings,
  tableTheme,
  backgroundColor,
  maxMobileColumns,
  maxDesktopColumns,
  titleRowIndices,
  className,
  variant,
}: CellDataProps) => {
  const getProductBySlug = useGetProductBySlug()
  const cellData = data[rowIndex][columnIndex]
  const matches = matchCellPatterns(cellData)
  let price = 0
  let priceResolved = false

  const bulletPoints = parseContent(matches.isBullet, 8)
  const list = parseContent(matches.isList, 6)
  let priceSubScript
  let priceSideScript
  let promotionSavings = 0
  let detailLink

  if (
    matches.isPriceFromStoryblokSlug ||
    matches.isDetailedPriceFromStoryblokSlug ||
    matches.isSimplePriceFromStoryblokSlug
  ) {
    const slug =
      matches.isDetailedPriceFromStoryblokSlug?.[1] ||
      matches.isPriceFromStoryblokSlug?.[1] ||
      matches.isSimplePriceFromStoryblokSlug?.[1] ||
      ""

    const item = getProductBySlug(slug)

    if (item) {
      price = Number(item.price)
      priceResolved = true

      if (matches.isSimplePriceFromStoryblokSlug) {
        const priceData = extractSimplePriceData(item)
        detailLink = priceData.detailLink
      }

      if (matches.isDetailedPriceFromStoryblokSlug) {
        const priceData = extractPriceData(item)
        priceSubScript = priceData.priceSubScript
        priceSideScript = priceData.priceSideScript
        promotionSavings = priceData.promotionSavings
      }
    }
  }
  const showPromotion = promotionSavings > 0
  const hasUnresolvedPrice =
    !priceResolved &&
    !!(
      matches.isPriceFromStoryblokSlug ||
      matches.isDetailedPriceFromStoryblokSlug ||
      matches.isSimplePriceFromStoryblokSlug
    )
  const { buttonLabel, buttonLink, secondButtonLabel, secondButtonLink } =
    parseButtonData(matches.isButton ? cellData.match(TableRegex.BUTTON) : null)

  const { textLinkLabel, textLinkUrl } = parseTextLinkData(
    matches.isTextLink ? cellData.match(TableRegex.TEXT_LINK) : null
  )

  const hasIcon =
    matches.isCheckIcon ||
    matches.isCrossIcon ||
    matches.isDashIcon ||
    matches.isCheckIconBlackStroke

  const hasIconWithText =
    matches.isCheckIcon?.[2] ||
    matches.isCrossIcon?.[1] ||
    matches.isDashIcon?.[2] ||
    matches.isCheckIconBlackStroke?.[2]

  const hasOtherColumnWithIconAndText = data[rowIndex].some(
    (cellContent, colIdx) => {
      if (colIdx === columnIndex) return false // Skip current column

      const otherMatches = matchCellPatterns(cellContent)
      return !!(
        otherMatches.isCheckIcon?.[2] ||
        otherMatches.isCrossIcon?.[1] ||
        otherMatches.isDashIcon?.[2] ||
        otherMatches.isCheckIconBlackStroke?.[2]
      )
    }
  )

  return (
    <div
      key={`column-${columnIndex}-row-${rowIndex}`}
      className={classNames(
        className,
        CELL_CLASSES,
        {
          "hidden sm:flex":
            displayIndex >= maxMobileColumns &&
            displayIndex < maxDesktopColumns,
          hidden: displayIndex >= maxDesktopColumns,
        },
        {
          "flex-col !items-start !gap-12":
            hasIconWithText || (hasIcon && hasOtherColumnWithIconAndText),
          "!pr-auto w-full": hasIcon,
          "flex-row-reverse items-center gap-8 sm:gap-32":
            hasIcon && !hasOtherColumnWithIconAndText && !hasIconWithText,
          "!justify-end":
            hasIcon &&
            !hasOtherColumnWithIconAndText &&
            !hasIconWithText &&
            variant === "doubleEntry",
          "flex-col gap-12": !hasIcon,
        },
        getCellRoundingClasses(displayIndex, maxDesktopColumns),
        {
          "mt-16 md:mt-24": titleRowIndices.current.find(
            (titleRowIndex) => titleRowIndex === rowIndex
          ),
          "justify-center":
            matches.isPriceFromStoryblokSlug ||
            matches.isDetailedPriceFromStoryblokSlug ||
            matches.isPrice,
        },
        getCellBackgroundClass(
          rowIndex,
          data.length,
          tableTheme,
          backgroundColor,
          !!matches.isButton
        ),
        { "!py-0": matches.isButton }
      )}
    >
      <div
        className={classNames(
          {
            "!hidden":
              matches.isPriceFromStoryblokSlug ||
              matches.isDetailedPriceFromStoryblokSlug ||
              matches.isPrice ||
              variant === "singleEntry",
          },
          CELL_TEXT_CLASSSES,
          "mt-[2px] flex text-left opacity-70"
        )}
      >
        <Typography
          font="grotesk"
          weight="book"
          color={getAccentColor(
            tableTheme === "light" ? Color.White : Color.Charcoal
          )}
          size="body-sm-fixed"
          text={
            rowHeadings[rowIndex].match(TableRegex.TITLE)
              ? ""
              : rowHeadings[rowIndex]
          }
        />
      </div>
      {hasUnresolvedPrice ? null : hasIcon ? (
        <IconCell matches={matches} theme={tableTheme} />
      ) : matches.isList && list ? (
        <ListCell items={list} theme={tableTheme} />
      ) : matches.isBullet && bulletPoints ? (
        <BulletListCell items={bulletPoints} theme={tableTheme} />
      ) : matches.isPriceFromStoryblokSlug ? (
        <PriceCell price={price} theme={tableTheme} />
      ) : matches.isDetailedPriceFromStoryblokSlug ? (
        <PriceCell
          price={price}
          theme={tableTheme}
          detailed
          priceSubScript={priceSubScript}
          priceSideScript={priceSideScript}
          promotionSavings={promotionSavings}
          showPromotion={showPromotion}
          topText={matches.isDetailedPriceFromStoryblokSlug?.[2]}
        />
      ) : matches.isButton && buttonLabel && buttonLink ? (
        <ButtonCell
          buttonLabel={buttonLabel}
          buttonLink={buttonLink}
          secondButtonLabel={secondButtonLabel}
          secondButtonLink={secondButtonLink}
          columnData={data[0][columnIndex]}
          theme={tableTheme}
        />
      ) : matches.isTextLink && textLinkLabel && textLinkUrl ? (
        <TextLinkCell
          textLinkLabel={textLinkLabel}
          textLinkUrl={textLinkUrl}
          columnData={data[0][columnIndex]}
          theme={tableTheme}
        />
      ) : matches.isSimplePriceFromStoryblokSlug ? (
        <SimplePriceFromStoryblokSlug
          price={price}
          iconLink={detailLink}
          theme={tableTheme}
        />
      ) : (
        !matches.isProduct &&
        cellData && (
          <>
            <Typography
              font="grotesk"
              weight={
                matches.isPrice || (rowIndex === 0 && variant === "singleEntry")
                  ? "medium"
                  : "book"
              }
              color={getThemeColor(tableTheme)}
              size={
                matches.isPrice
                  ? "lead-lg"
                  : rowIndex === 0
                    ? "body-lg"
                    : "body-md"
              }
              text={
                matches.isPrice ? extractFromTag(cellData, "price") : cellData
              }
              className={classNames(
                { "!text-center": variant === "singleEntry" },
                CELL_TEXT_CLASSSES
              )}
            />
          </>
        )
      )}
    </div>
  )
}

export default CellData
